import { AxiosResponse } from "axios";
import {
  CalendarEntity,
  EducationLevel,
  InitializeCalendarRequest,
  UpdateWeekDateRequest,
  UpdateMultipleWeeksRequest,
  ResolvedClassroomCalendar,
} from "./calendar.types";
import { api } from "../api";

// General-calendar services are always scoped by education level (SCRUM-744):
// the level travels as a query param on the read and inside the body of the
// write requests, which the callers build.
export const getCalendar = async (
  educationLevel: EducationLevel
): Promise<CalendarEntity[]> => {
  try {
    const response: AxiosResponse<CalendarEntity[]> = await api.get(
      "/calendar",
      { params: { education_level: educationLevel } }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching calendar:", error);
    throw error;
  }
};

export const initializeCalendar = async (
  data: InitializeCalendarRequest
): Promise<CalendarEntity[]> => {
  try {
    const response: AxiosResponse<CalendarEntity[]> = await api.post(
      "/calendar/initialize",
      data
    );
    return response.data;
  } catch (error: any) {
    console.error("Error initializing calendar:", error);
    throw error;
  }
};

export const updateSingleWeekDate = async (
  data: UpdateWeekDateRequest
): Promise<CalendarEntity[]> => {
  try {
    const response: AxiosResponse<CalendarEntity[]> = await api.patch(
      "/calendar/week/update-date",
      data
    );
    return response.data;
  } catch (error: any) {
    console.error("Error updating single week date:", error);
    throw error;
  }
};

export const updateMultipleWeekDates = async (
  data: UpdateMultipleWeeksRequest
): Promise<CalendarEntity[]> => {
  try {
    const response: AxiosResponse<CalendarEntity[]> = await api.patch(
      "/calendar/week/update-date",
      data
    );
    return response.data;
  } catch (error: any) {
    console.error("Error updating multiple week dates:", error);
    throw error;
  }
};

// School Calendar Services — always scoped by education level (SCRUM-842):
// a school keeps one custom calendar per section, so omitting the level would
// fall back to the backend's legacy behavior (mixed schools → PRIMARIA).
export const getSchoolCalendar = async (
  schoolId: number,
  educationLevel: EducationLevel
): Promise<CalendarEntity[]> => {
  try {
    const response: AxiosResponse<CalendarEntity[]> = await api.get(
      `/calendar/school/${schoolId}`,
      { params: { education_level: educationLevel } }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching school calendar:", error);
    throw error;
  }
};

export const initializeSchoolCalendar = async (
  schoolId: number,
  educationLevel: EducationLevel
): Promise<CalendarEntity[]> => {
  try {
    const response: AxiosResponse<CalendarEntity[]> = await api.post(
      `/calendar/school/${schoolId}/initialize`,
      { education_level: educationLevel }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error initializing school calendar:", error);
    throw error;
  }
};

export const updateSchoolSingleWeekDate = async (
  data: UpdateWeekDateRequest & {
    school_id: number;
    education_level: EducationLevel;
  }
): Promise<CalendarEntity[]> => {
  try {
    const response: AxiosResponse<CalendarEntity[]> = await api.patch(
      "/calendar/week/update-date",
      data
    );
    return response.data;
  } catch (error: any) {
    console.error("Error updating school single week date:", error);
    throw error;
  }
};

export const updateSchoolMultipleWeekDates = async (
  data: UpdateMultipleWeeksRequest & {
    school_id: number;
    education_level: EducationLevel;
  }
): Promise<CalendarEntity[]> => {
  try {
    const response: AxiosResponse<CalendarEntity[]> = await api.patch(
      "/calendar/week/update-date",
      data
    );
    return response.data;
  } catch (error: any) {
    console.error("Error updating school multiple week dates:", error);
    throw error;
  }
};

export const syncSchoolCalendarWithGeneral = async (
  schoolId: number,
  educationLevel: EducationLevel
): Promise<CalendarEntity[]> => {
  try {
    const response: AxiosResponse<CalendarEntity[]> = await api.patch(
      `/calendar/school/${schoolId}/sync-with-general`,
      undefined,
      { params: { education_level: educationLevel } }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error syncing school calendar with general:", error);
    throw error;
  }
};

export const syncClassroomCalendarWithGeneral = async (
  schoolId: number,
  classroomId: number
): Promise<CalendarEntity[]> => {
  try {
    const response: AxiosResponse<CalendarEntity[]> = await api.patch(
      `/calendar/school/${schoolId}/sync-with-general?classroomId=${classroomId}`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error syncing classroom calendar with general:", error);
    throw error;
  }
};

export const syncClassroomCalendarWithSchool = async (
  schoolId: number,
  classroomId: number
): Promise<CalendarEntity[]> => {
  try {
    const response: AxiosResponse<CalendarEntity[]> = await api.patch(
      `/calendar/school/${schoolId}/classroom/${classroomId}/sync-with-school`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error syncing classroom calendar with school:", error);
    throw error;
  }
};

// Classroom Calendar Services - Operaciones en bloque. Classroom calendars
// are NOT scoped by education level (a classroom already belongs to one), so
// none of these carry education_level.
export const divideCalendarByClassrooms = async (
  schoolId: number,
  classroomIds: number[]
): Promise<void> => {
  try {
    await Promise.all(
      classroomIds.map(async (classroomId) => {
        try {
          await api.patch(
            `/calendar/school/${schoolId}/classroom/${classroomId}/reactivate`
          );
        } catch {
          await api.post(`/calendar/school/${schoolId}/classroom/${classroomId}`);
        }
      })
    );
  } catch (error: any) {
    console.error("Error dividing calendar by classrooms:", error);
    throw error;
  }
};

export const unifyCalendarsByClassrooms = async (
  schoolId: number,
  classroomIds: number[]
): Promise<void> => {
  try {
    await Promise.all(
      classroomIds.map((classroomId) =>
        api.patch(
          `/calendar/school/${schoolId}/classroom/${classroomId}/deactivate`
        )
      )
    );
  } catch (error: any) {
    console.error("Error unifying calendars by classrooms:", error);
    throw error;
  }
};

export const getClassroomCalendar = async (
  schoolId: number,
  classroomId: number
): Promise<CalendarEntity[]> => {
  try {
    const response: AxiosResponse<CalendarEntity[]> = await api.get(
      `/calendar/school/${schoolId}/classroom/${classroomId}`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching classroom calendar:", error);
    throw error;
  }
};

export const updateClassroomCalendarDates = async (
  schoolId: number,
  classroomId: number,
  data: UpdateWeekDateRequest | UpdateMultipleWeeksRequest
): Promise<CalendarEntity[]> => {
  try {
    const weeks =
      "weeks" in data
        ? data.weeks
        : [{ week: data.week, start_date: data.start_date }];

    const response: AxiosResponse<CalendarEntity[]> = await api.patch(
      "/calendar/classroom/dates",
      {
        school_id: schoolId,
        classroom_id: classroomId,
        weeks,
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error updating classroom calendar dates:", error);
    throw error;
  }
};

export const createClassroomCalendar = async (
  schoolId: number,
  classroomId: number
): Promise<CalendarEntity[]> => {
  try {
    const response: AxiosResponse<CalendarEntity[]> = await api.post(
      `/calendar/school/${schoolId}/classroom/${classroomId}`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error creating classroom calendar:", error);
    throw error;
  }
};

export const deactivateClassroomCalendar = async (
  schoolId: number,
  classroomId: number
): Promise<CalendarEntity[]> => {
  try {
    const response: AxiosResponse<CalendarEntity[]> = await api.patch(
      `/calendar/school/${schoolId}/classroom/${classroomId}/deactivate`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error deactivating classroom calendar:", error);
    throw error;
  }
};

export const reactivateClassroomCalendar = async (
  schoolId: number,
  classroomId: number
): Promise<CalendarEntity[]> => {
  try {
    const response: AxiosResponse<CalendarEntity[]> = await api.patch(
      `/calendar/school/${schoolId}/classroom/${classroomId}/reactivate`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error reactivating classroom calendar:", error);
    throw error;
  }
};

// Program override services (classroom-group-model, PR-S2c-FE) — the
// per-materia calendar override for a given classroom. `classroomId` is the
// existing classroom row; `programId` scopes the override tier independently
// of that classroom's own native program_id.
export const getResolvedClassroomCalendar = async (
  classroomId: number,
  programId?: number
): Promise<ResolvedClassroomCalendar> => {
  try {
    const response: AxiosResponse<ResolvedClassroomCalendar> = await api.get(
      `/calendar/classroom/${classroomId}`,
      { params: programId ? { program_id: programId } : undefined }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching resolved classroom calendar:", error);
    throw error;
  }
};

export const createProgramCalendarOverride = async (
  classroomId: number,
  programId: number
): Promise<CalendarEntity[]> => {
  try {
    const response: AxiosResponse<CalendarEntity[]> = await api.post(
      `/calendar/classroom/${classroomId}/program/${programId}`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error creating program calendar override:", error);
    throw error;
  }
};

export const removeProgramCalendarOverride = async (
  classroomId: number,
  programId: number
): Promise<void> => {
  try {
    await api.delete(`/calendar/classroom/${classroomId}/program/${programId}`);
  } catch (error: any) {
    console.error("Error removing program calendar override:", error);
    throw error;
  }
};

export const updateProgramCalendarWeekDate = async (
  classroomId: number,
  programId: number,
  data: UpdateWeekDateRequest | UpdateMultipleWeeksRequest
): Promise<CalendarEntity[]> => {
  try {
    const response: AxiosResponse<CalendarEntity[]> = await api.patch(
      "/calendar/week/update-date",
      { ...data, classroom_id: classroomId, program_id: programId }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error updating program calendar week date:", error);
    throw error;
  }
};

// Función auxiliar para verificar si una escuela tiene calendarios divididos
export const checkSchoolDivisionStatus = async (
  schoolId: number,
  classroomIds: number[]
): Promise<{ isDivided: boolean; dividedClassrooms: number[] }> => {
  try {
    const results = await Promise.all(
      classroomIds.map(async (classroomId) => {
        try {
          const calendar = await getClassroomCalendar(schoolId, classroomId);
          return (
            calendar.length > 0 &&
            calendar[0].calendar_type === "CLASSROOM" &&
            calendar[0].classroom_id === classroomId &&
            calendar[0].deleted_at === null
          );
        } catch {
          return false;
        }
      })
    );

    const dividedClassrooms = classroomIds.filter((_, index) => results[index]);
    return {
      isDivided: dividedClassrooms.length > 0,
      dividedClassrooms,
    };
  } catch (error: any) {
    console.error("Error checking school division status:", error);
    return { isDivided: false, dividedClassrooms: [] };
  }
};
